/**
 * actions.js
 * Contains all logic, see actionTypes.js for high-level description of each action type.
 */
import React from "react";
import { Modal, Button } from "antd";
import { getToken } from "./authActions";
import * as types from "./actionTypes";

export const getDataSuccess = data => {
  return { type: types.GET_DATA_SUCCESS, data };
};

export const postDataSuccess = data => {
  return { type: types.POST_DATA_SUCCESS, data };
};

export const setProfileIndex = index => {
  return { type: types.SET_PROFILE_INDEX, index };
};

export const getJobSuccess = job => {
  return { type: types.GET_JOB_SUCCESS, job };
};

export const result = result => {
  return { type: types.RESULT, result };
};

export const submitProfile =
  ({ accepted }) =>
  (dispatch, getState) => {
    const state = getState();

    const user = state.data.users[state.data.profileIndex];

    const mongoPutBody = {
      accepted,
    }; // init body to submit

    //BUILD MONGO
    dispatch(
      putData({
        _id: user._id,
        batchId: user.batchId,
        putBody: mongoPutBody,
      })
    ); //save to db

    //if last index, get more data
    if (state.data.profileIndex >= state.data.users.length - 1)
      dispatch(progressData(state.data.job));
    else dispatch(setProfileIndex()); // next profile
  };

// Fetch fresh thumbnail URLs for user shortcodes from Facebook API.
export const refreshThumbnails = (userId, batchId) => async dispatch => {
  const token = await dispatch(getToken());
  const body = { userId, batchId };

  // Retry 3x, put up global error if it doesn't work.
  let retryAttempts = 3;
  let lastError;

  while (retryAttempts > 0) {
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_API_HOST}api/services/instagram/refreshThumbnails`,
        {
          method: "PUT",
          body: JSON.stringify(body),
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json; charset=utf-8",
          },
        }
      );

      const freshThumbnails = await resp.json();
      dispatch(setUserThumbnails(userId, freshThumbnails));
      break;
    } catch (e) {
      lastError = e;
      console.error(e);
      retryAttempts--;
    }
  }

  // Failed to refresh thumbnails, crash >:(
  if (retryAttempts === 0) {
    dispatch(
      result({
        status: "error",
        title: "Oops, something went wrong",
        subtitle: lastError.message || null,
      })
    );
  }
};

const setUserThumbnails = (userId, thumbnails) => {
  return { type: types.SET_USER_THUMBNAILS, userId, thumbnails };
};

// Log job progress in batches of 10.
export const progressData =
  (job, retryAttempts = 1) =>
  async (dispatch, getState) => {
    const state = getState();
    const firstLoad = !(job && state.data.users && state.data.users.length);
    const token = await dispatch(getToken());
    const { batchId } = state.auth; //batchId from state
    return fetch(
      `${process.env.REACT_APP_API_HOST}api/scrapestats/${batchId}/jobs`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json; charset=utf-8",
        },
        //if users already loaded, add progress and get next page
        body: firstLoad
          ? undefined
          : JSON.stringify({
              progress: job.progress + state.data.users.length, //progress based on current amount of users loaded
            }),
      }
    )
      .then(async res => {
        const json = await res.json();
        if (!res.ok) throw new Error(json.message || res.statusText);
        const { job, users } = json;
        dispatch(getJobSuccess(job));
        if (job.done)
          return dispatch(
            result({
              status: "success",
              title: "Thank you! This job is now complete.",
            })
          );

        /**
         * Show training Contents including Video and Flowchart
         * if first successful load of job
         */
        if (firstLoad && users) {
          Modal.info({
            centered: true,
            width: 840,
            title: "Please refer to Training Video and Flowchart",
            content: (
              <div>
                <video width="720" controls>
                  <source
                    src={`${process.env.REACT_APP_EXAMPLE_HOST_URL}video.mp4`}
                    type="video/mp4"
                  />
                </video>
                <a
                  target="_blank"
                  href={`${process.env.REACT_APP_EXAMPLE_HOST_URL}flowchart.png`}
                  rel="noreferrer"
                >
                  <Button size="large" type="secondary" icon="branches">
                    View Decision Flowchart
                  </Button>
                </a>
              </div>
            ),
            okText: "Close",
          });
        }

        //reset users to validate
        dispatch(getDataSuccess(users));
        dispatch(setProfileIndex(0)); //reset profileIndex
      })
      .catch(err => {
        //error handling
        if (retryAttempts > 0)
          return dispatch(progressData(job, retryAttempts - 1));
        dispatch(
          result({
            status: "error",
            title: "Oops, something went wrong",
            subtitle: err.message || null,
          })
        );
      });
  };

// Validate a single user.
const putData =
  (body, retryAttempts = 1) =>
  async (dispatch, getState) => {
    const token = await dispatch(getToken());
    return fetch(`${process.env.REACT_APP_API_HOST}api/users/validate`, {
      method: "PUT",
      body: JSON.stringify(body), //from submitProfile action
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json; charset=utf-8",
      },
    })
      .then(async res => {
        if (!res.ok) throw new Error(res.statusText);
      })
      .catch(async err => {
        //report error handling
        if (retryAttempts > 0)
          return dispatch(putData(body, retryAttempts - 1));
      });
  };
